
import React, { useContext, useRef, useState } from 'react';
import { AngularServicesContext } from 'react-app';
import t from 'react-translate';
import { css } from '@emotion/react';

// redux
import { useSelector } from 'react-redux';
import { getCurrentCourse } from 'redux/selectors/course';

// styles
import { halfSpacing, journeyCardWidth } from 'styles/global_defaults/scaffolding';

// hooks
import useClickOutside from 'shared/hooks/use-click-outside';
import { useLecturePageFromParams } from 'lecture_pages/hooks/lecture-routing';

// components
import ClickableContainer from 'components/clickable-container';
import NvPopover from 'shared/components/nv-popover';
import NvIcon from 'shared/components/nv-icon';

export const LecturePrerequisiteLockButton = () => {
  const { CurrentPermissionsManager, RailsRoutes, InteroperabilityRoutes } = useContext(AngularServicesContext);
  const lecturePage = useLecturePageFromParams();
  const catalogId = useSelector((state) => state.app.currentCatalogId);
  const course = useSelector((state) => getCurrentCourse(state));
  const popoverRef = useRef(null);
  const [showLockPopover, setShowLockPopover] = useState(false);

  const styles = css`
    .lock-icon {
      border-radius: 100%;
    }
  `;

  const overlayStyles = css`
    max-width: ${journeyCardWidth}px;

    .bs4-popover-body {
      padding: ${halfSpacing}px;
    }
  `;

  useClickOutside(popoverRef, () => {
    if (showLockPopover) {
      setShowLockPopover(false);
    }
  });

  const getPopoverText = () => {
    let popoverText = '';
    const params = {
      learnersAlias: course.learnersName.downcasedPluralized,
      activityAlias: course.lectureName.downcasedSingularized,
      action: 'other',
    };

    if (lecturePage.studentPrerequisites.length === 1) {
      popoverText = t.ACTIVITY.LOCKED_POPOVER_SINGLE({
        ...params,
        activityName: lecturePage.studentPrerequisites[0].title,
        activityLink: `${InteroperabilityRoutes.activityPath(
          course,
          lecturePage.studentPrerequisites[0],
          CurrentPermissionsManager.hasCourseAdminPermissions(),
        )}`,
      });
    } else {
      const translateText = lecturePage.studentPrerequisites[0].type === lecturePage.studentPrerequisites[1].type
        ? t.ACTIVITY.LOCKED_POPOVER_MULTIPLE_SAME
        : t.ACTIVITY.LOCKED_POPOVER_MULTIPLE_DIFFERENT;

      popoverText = translateText({
        ...params,
        action1: 'other',
        activityFirstName: lecturePage.studentPrerequisites[0].title,
        activityFirstLink: `${InteroperabilityRoutes.activityPath(
          course,
          lecturePage.studentPrerequisites[0],
          CurrentPermissionsManager.hasCourseAdminPermissions(),
        )}`,
        activitySecondName: lecturePage.studentPrerequisites[1].title,
        activitySecondLink: `${InteroperabilityRoutes.activityPath(
          course,
          lecturePage.studentPrerequisites[1],
          CurrentPermissionsManager.hasCourseAdminPermissions(),
        )}`,
      });
    }

    return popoverText;
  };

  return (
    <div css={styles}>
      {lecturePage?.studentPrerequisites?.length > 0 && (
        <ClickableContainer className='mr-2'>
          <NvPopover
            placement='bottom-end'
            overlayStyles={overlayStyles}
            content={(
              <div className='text-small text-center font-weight-bold' ref={popoverRef}>
                <div>{getPopoverText()}</div>
                <br />
                <a
                  href={RailsRoutes.adminDashboardPath(catalogId, true)}
                  className='text-primary'
                >
                  {t.ACTIVITY.EDIT_PREREQUISITES()}
                </a>
              </div>
            )}
            show={showLockPopover}
          >
            <NvIcon
              icon='locked'
              size='xss-smallest'
              onClick={() => setShowLockPopover(!showLockPopover)}
              className='lock-icon text-white p-2 bg-black'
            />
          </NvPopover>
        </ClickableContainer>
      )}
    </div>
  );
};

export default LecturePrerequisiteLockButton;
